import * as React from 'react';
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {Navigate} from "react-router-dom";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import {
    FalconPaymentRequest,
    FalconPaymentResponse,
    InstalmentsInfo,
    INTERACTION_PROCESS_MTA_ADD_PAY_OFF_BALANCE,
    Party,
    PaymentCard,
    Policy,
    PolicyPropertyEnum,
    ProductsEnum,
} from "@ferry/journey-services";
import {CardPaymentPanel} from "../../../../../components/payments/CardPaymentPanel";
import {lazyInject} from "@athena-portal/core";
import {findCardToReuse, getAmountWithDecimalDigits} from "../../../../../CommonFunctions";
import {FailedPaymentMessage} from "../../../../../components/payments/FailedPaymentMessage";
import {Types} from "../../../../../Types";
import type {ICustomerPortalService} from "../../../../../services/ICustomerPortalService";
import {WarningBanner, FalconNavigatorHeader, Footer} from "@ferry/portal-core";

interface IParams {
    policyNumber: string;
    [key: string]: string | undefined
}

interface IState {
    payOffAmount?: number;
    party?: Party;
    paymentFailed: boolean;
    showOverlay: boolean;
    reuseCard?: boolean;
    mainCardId?: number;
    inFlightInstalmentPremium?: number;
    remainingInterest?: number;
    policy?: Policy,
}

@authorize
export class PayOffBalancePagePlain extends React.Component<IWithRouterProps<IParams>, IState> {

    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IWithRouterProps<IParams>) {
        super(props);

        this.state = {
            paymentFailed: false,
            showOverlay: false
        }

        this.handleExistingCardPayoff = this.handleExistingCardPayoff.bind(this);
        this.cancelChanges = this.cancelChanges.bind(this);
    }

    public componentDidMount(): void {
        this.customerPortalService.getProfile().subscribe((output: Party) => {
            this.setState({
                party: output
            })
        });

        this.customerPortalService.getInstalmentsInfo(this.props.params.policyNumber, false).subscribe((output: InstalmentsInfo) => {
            let remainingInterestAmount: number | undefined = output.remainingInterest;
            const inflightInterest: number | undefined = output.inflightInterest;

            if (inflightInterest !== undefined && remainingInterestAmount !== undefined) {
                remainingInterestAmount += inflightInterest;
            }

            this.setState({
                payOffAmount: output.payOffAmount,
                remainingInterest: remainingInterestAmount,
                // Realistically there should only be 1 IN_FLIGHT invoice/instalment
                inFlightInstalmentPremium: output.instalments.filter(instalment => instalment.status === "IN_FLIGHT").map(instalment => instalment.amount).reduce((a, b) => a + b, 0),
            });
        });

        this.customerPortalService.getPolicy(this.props.params.policyNumber).subscribe(policy => {
            policy.policyProperties.forEach(property => {
                if (property.key === PolicyPropertyEnum.MAIN_ANNUAL_PAYMENT_CARD) {
                    this.setState({mainCardId: +property.value});
                } else if (property.key === PolicyPropertyEnum.REUSE_MAIN_CARD) {
                    this.setState({reuseCard: property.value === "true"})
                }
            });

                this.setState({policy});
            }
        )


    }

    public render(): React.ReactNode {
        const backLinkUrl: string = `/dashboard/monthlyPaymentHistory/${this.props.params.policyNumber}`;

        if (this.state.party && this.state.payOffAmount && this.state.policy) {
            const mainCard: PaymentCard | undefined = findCardToReuse(this.state.party, this.state.reuseCard, this.state.mainCardId);

            if (mainCard) {
                return (
                    <>
                        <FalconHeader/>
                        {this.state.paymentFailed && <FailedPaymentMessage/>}
                        <div className="page-contents">
                            <FalconNavigatorHeader back={backLinkUrl} title="Pay off my balance"/>
                            {!!this.state.inFlightInstalmentPremium &&
                                <div className="margin-bottom-32">
                                    <WarningBanner wording={<>Please don't cancel your Direct Debit until your next instalment is collected.</>}/>
                                </div>
                            }
                            <div className="panel policy-change-price">
                                <div className="price font-family-display font-weight-bold text-center">
                                    {getAmountWithDecimalDigits(this.state.payOffAmount)}
                                </div>
                                <p className="text-center margin-bottom-24">to pay in full for your car insurance.
                                    {!!this.state.inFlightInstalmentPremium ? ` Please note this excludes your next instalment
                                    of ${getAmountWithDecimalDigits(this.state.inFlightInstalmentPremium)}.` : ""}</p>
                                <p className="text-center margin-bottom-24">By paying off your car insurance, you're
                                    saving {getAmountWithDecimalDigits(this.state.remainingInterest)} in interest charges. There
                                    are no fees on doing that.</p>
                                <p className="text-center margin-bottom-24">All figures include Insurance Premium
                                    Tax.</p>
                            </div>
                            <CardPaymentPanel amount={this.state.payOffAmount}
                                party={this.state.party}
                                handleExistingCardPayment={this.handleExistingCardPayoff}
                                newCardUrl={`/dashboard/${this.props.params.policyNumber}/payOffBalance/newCard`}
                                mainCard={mainCard}
                            />
                            <button type="button"
                                    className="btn btn-default btn-white-dashboard m-auto"
                                    onClick={this.cancelChanges}>
                                Cancel changes
                            </button>
                            <div>
                                {this.pluginTemplates.renderBackLink("Back", false, backLinkUrl)}
                            </div>
                        </div>
                        <Footer/>
                        <Overlay show={this.state.showOverlay}/>
                    </>
                )
            } else {
                return (
                    <Navigate to={{
                        pathname: `/dashboard/${this.props.params.policyNumber}/payOffBalance/newCard`
                    }} state={{
                        paymentFailed: false,
                        payOffAmount: this.state.payOffAmount
                    }}/>
                )
            }
        } else {
            return <Overlay show={true}/>
        }
    }

    private handleExistingCardPayoff(event: any, cardId: number | undefined): void {
        event.preventDefault();

        this.setState({
            showOverlay: true
        })

        const request: FalconPaymentRequest = new FalconPaymentRequest();
        request.invoiceType = "PAYOFF";
        request.cardId = cardId;
        request.product = ProductsEnum.WOLF_MOTOR;

        this.customerPortalService.payOffBalance(request, this.props.params.policyNumber).subscribe((response: FalconPaymentResponse) => {
            if (response.success) {
                window.sessionStorage.setItem("showPopup", "true");
                window.sessionStorage.setItem("paymentAmount", getAmountWithDecimalDigits(this.state.payOffAmount).toString());
                window.sessionStorage.setItem("referrerJourney", INTERACTION_PROCESS_MTA_ADD_PAY_OFF_BALANCE);

                window.location.href = `/dashboard/monthlyPaymentHistory/${this.props.params.policyNumber}`;
            } else {
                this.setState({
                    paymentFailed: true
                })
            }

            this.setState({
                showOverlay: false
            })
        })
    }

    private cancelChanges(): void {
        this.props.navigate(`/dashboard/monthlyPaymentHistory/${this.props.params.policyNumber}`);
    }

}

export const PayOffBalancePage = withRouter<{}, IParams>(PayOffBalancePagePlain)
